import React, { useState } from 'react';
import { useAuditLog } from '../../hooks/useAuditLog';
import { Card } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { AuditLogTable } from '../../components/security/AuditLogTable';
import { AuditLogDetail } from '../../components/security/AuditLogDetail';
import {
  AuditAction,
  AuditEntityType,
  AUDIT_ACTION_LABELS,
  AUDIT_ENTITY_LABELS,
} from '../../types/audit';
import { History, Download, RotateCcw } from 'lucide-react';

const toCsv = (rows: Record<string, any>[]): string => {
  if (rows.length === 0) return '';
  const headers = Object.keys(rows[0]);
  const escape = (v: any) => {
    const s = v === null || v === undefined ? '' : typeof v === 'object' ? JSON.stringify(v) : String(v);
    return `"${s.replace(/"/g, '""')}"`;
  };
  return [
    headers.join(','),
    ...rows.map(r => headers.map(h => escape(r[h])).join(',')),
  ].join('\n');
};

export const AuditLogsPage: React.FC = () => {
  const {
    logs, total, totalPages, filter, isLoading, selectedLog, setSelectedLog,
    updateFilter, resetFilter, exportLogs,
  } = useAuditLog();
  const [isExporting, setIsExporting] = useState(false);

  const handleExport = async () => {
    setIsExporting(true);
    try {
      const rows = await exportLogs();
      const csv = toCsv(rows.map(r => ({
        log_id: r.log_id,
        timestamp: r.timestamp,
        user_id: r.user_id,
        user_name: r.user_name,
        action: r.action,
        entity_type: r.entity_type,
        entity_id: r.entity_id,
        ip_address: r.ip_address,
        description: r.description,
      })));
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `audit-logs-${new Date().toISOString().slice(0, 10)}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <div className="space-y-5">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h3 className="text-lg font-semibold text-slate-100 flex items-center gap-2">
            <History className="w-5 h-5 text-sky-400" />
            Audit Logs
          </h3>
          <p className="text-xs text-slate-400">Complete system-wide activity trail for compliance and security</p>
        </div>
        <Button variant="outline" size="sm" isLoading={isExporting} onClick={handleExport} icon={<Download className="w-3.5 h-3.5" />}>
          Export CSV
        </Button>
      </div>

      <Card>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3 mb-4">
          <Input
            type="date"
            label="From"
            value={filter.date_from || ''}
            onChange={e => updateFilter({ date_from: e.target.value })}
          />
          <Input
            type="date"
            label="To"
            value={filter.date_to || ''}
            onChange={e => updateFilter({ date_to: e.target.value })}
          />
          <div>
            <label className="block text-xs font-medium text-slate-400 mb-1">Action</label>
            <select
              value={filter.action || ''}
              onChange={e => updateFilter({ action: e.target.value as AuditAction | '' })}
              className="w-full bg-slate-800 border border-slate-700 rounded-lg px-3 py-2.5 text-xs text-slate-100 focus:outline-none focus:border-sky-500"
            >
              <option value="">All Actions</option>
              {(Object.keys(AUDIT_ACTION_LABELS) as AuditAction[]).map(a => (
                <option key={a} value={a}>{AUDIT_ACTION_LABELS[a]}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-slate-400 mb-1">Entity</label>
            <select
              value={filter.entity_type || ''}
              onChange={e => updateFilter({ entity_type: e.target.value as AuditEntityType | '' })}
              className="w-full bg-slate-800 border border-slate-700 rounded-lg px-3 py-2.5 text-xs text-slate-100 focus:outline-none focus:border-sky-500"
            >
              <option value="">All Entities</option>
              {(Object.keys(AUDIT_ENTITY_LABELS) as AuditEntityType[]).map(t => (
                <option key={t} value={t}>{AUDIT_ENTITY_LABELS[t]}</option>
              ))}
            </select>
          </div>
          <Input
            label="Search"
            placeholder="Entity ID, user, detail..."
            value={filter.search || ''}
            onChange={e => updateFilter({ search: e.target.value })}
          />
        </div>

        <div className="flex justify-end mb-3">
          <button
            onClick={resetFilter}
            className="inline-flex items-center gap-1.5 text-xs text-slate-400 hover:text-slate-200"
          >
            <RotateCcw className="w-3.5 h-3.5" /> Reset Filters
          </button>
        </div>

        <AuditLogTable
          logs={logs}
          isLoading={isLoading}
          page={filter.page || 1}
          totalPages={totalPages}
          total={total}
          limit={filter.limit || 25}
          onPageChange={page => updateFilter({ page })}
          onSelect={setSelectedLog}
        />
      </Card>

      <AuditLogDetail log={selectedLog} onClose={() => setSelectedLog(null)} />
    </div>
  );
};
